<script setup lang="ts">
import {
  type EnumResponseType,
  type EnumResponseTypeValue,
  type PromptInDb,
  type PromptUpdate,
  SuggestionTools,
} from '@/api';
import AgentDrawerSidebarActions from '@/components/AgentDrawerSidebarActions.vue';
import { IconClose } from '@/components/design/icons';
import { useAuthStore } from '@/stores/auth';
import { computed, ref, watch } from 'vue';
import { logger } from '@/utils/logger';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaCheckboxGroup from '@/components/design/base/GaiaCheckboxGroup.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

type NonEnumResponseType = Exclude<PromptInDb['response_type'], EnumResponseType>;
type NonEnumEvaluationResponseType = Exclude<
  PromptInDb['evaluation_response_type'],
  EnumResponseType | null
>;

const props = defineProps<{
  show: boolean;
  prompt: PromptInDb | null;
  isNew: boolean;
  collectionId?: string;
  saveNotice?: string;
  saveDisabled?: boolean;
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  'draft-agent-count-change': [count: number];
  save: [promptId: string, updates: PromptUpdate];
}>();

const message = useGaiaToast();
const authStore = useAuthStore();
const canSavePrompt = computed(() => authStore.userPermissions.is_org_admin && !props.saveDisabled);

// Local state for the drawer
const showDrawer = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});
// Expand modal state
const showExpandModal = ref(false);
const expandedTextareaType = ref<'extraction' | 'evaluation' | 'suggestion' | null>(null);
const expandedText = ref('');

// Extraction agent state
const extractionPrompt = ref('');
const extractionResponseTypeDisplay = ref<string>('');
const extractionName = ref('');
const extractionExampleData = ref<string[]>([]);

// Evaluation agent state
const evaluationPrompt = ref<string | null>(null);
const evaluationResponseTypeDisplay = ref<string | null>(null);

// Suggestion agent state
const suggestionPrompt = ref<string | null>(null);
const suggestionExampleData = ref<string[]>([]);
const suggestionToolSelection = ref<SuggestionTools[]>([SuggestionTools.READ_CLAUSE_LIBRARY]);

// Enum options for both agents
const extractionEnumOptions = ref<EnumResponseTypeValue[]>([]);
const evaluationEnumOptions = ref<EnumResponseTypeValue[]>([]);

const drawerWidth = '800px';

// Computed properties for type options
const typeOptions = [
  { label: 'Text', value: 'str' },
  { label: 'List of Text', value: 'list[str]' },
  { label: 'Number', value: 'float' },
  { label: 'List of Numbers', value: 'list[float]' },
  { label: 'Yes/No', value: 'bool' },
  { label: 'List of Yes/No', value: 'list[bool]' },
  { label: 'Date', value: 'date' },
  { label: 'List of Dates', value: 'list[date]' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'List of Date & Time', value: 'list[datetime]' },
  { label: 'Choice (Single)', value: 'enum' },
  { label: 'Choice (Multiple)', value: 'multiple_enum' },
  { label: 'Number with Currency', value: 'CurrencyAmount' },
  { label: 'List of Numbers with Currency', value: 'list[CurrencyAmount]' },
  { label: 'Person or Organization with Address', value: 'Entity' },
  { label: 'List of Organizations or Persons with Address', value: 'list[Entity]' },
  { label: 'Calendar Schedules', value: 'CalendarEvents' },
];

// Check if extraction agent is using enum type
const isExtractionEnumType = computed<boolean>(() => {
  return (
    extractionResponseTypeDisplay.value === 'enum' ||
    extractionResponseTypeDisplay.value === 'multiple_enum'
  );
});

// Check if evaluation agent is using enum type
const isEvaluationEnumType = computed<boolean>(() => {
  return (
    evaluationResponseTypeDisplay.value === 'enum' ||
    evaluationResponseTypeDisplay.value === 'multiple_enum'
  );
});

// Check if evaluation agent exists
const hasEvaluationAgent = computed<boolean>(() => {
  return evaluationPrompt.value !== null;
});

// Check if suggestion agent exists
const hasSuggestionAgent = computed<boolean>(() => {
  return suggestionPrompt.value !== null;
});

const activeAgentCount = computed<number>(() => {
  return 1 + (hasEvaluationAgent.value ? 1 : 0) + (hasSuggestionAgent.value ? 1 : 0);
});

// Active tab state
const activeTab = ref<string>('extraction');

watch(
  () => activeAgentCount.value,
  count => {
    emit('draft-agent-count-change', count);
  },
  { immediate: true }
);

// Watch for when evaluation agent is deleted and switch to extraction if on evaluation tab
watch(
  () => hasEvaluationAgent.value,
  hasEval => {
    if (!hasEval && activeTab.value === 'evaluation') {
      activeTab.value = 'extraction';
    }
  }
);

// Watch for when suggestion agent is deleted and switch to extraction if on suggestion tab
watch(
  () => hasSuggestionAgent.value,
  hasSuggestion => {
    if (!hasSuggestion && activeTab.value === 'suggestion') {
      activeTab.value = 'extraction';
    }
  }
);

// Watch for when drawer opens and ensure we're on a valid tab
watch(
  () => props.show,
  isShown => {
    if (isShown) {
      // If current tab is evaluation but no evaluation agent exists, switch to extraction
      if (activeTab.value === 'evaluation' && !hasEvaluationAgent.value) {
        activeTab.value = 'extraction';
      }
      // If current tab is suggestion but no suggestion agent exists, switch to extraction
      if (activeTab.value === 'suggestion' && !hasSuggestionAgent.value) {
        activeTab.value = 'extraction';
      }
    }
  }
);

// When the drawer is opened, always show extraction agent in edit mode for new prompts
watch(
  () => props.show,
  shown => {
    if (shown && props.isNew) {
      // No longer need to set editing state since forms are always editable
    }
  },
  { immediate: true }
);

// Watch for changes in the prompt prop to update local state
watch(
  () => props.prompt,
  newPrompt => {
    if (newPrompt) {
      // Initialize extraction agent fields
      extractionName.value = newPrompt.name;
      extractionPrompt.value = newPrompt.prompt;
      extractionExampleData.value = newPrompt.example_data ?? [];

      // Handle response type and enum options for extraction agent
      if (typeof newPrompt.response_type === 'object' && 'values' in newPrompt.response_type) {
        // For enum types, set the display value and preserve the original object
        extractionResponseTypeDisplay.value = newPrompt.response_type.multiple_choices
          ? 'multiple_enum'
          : 'enum';
        extractionEnumOptions.value = [...newPrompt.response_type.values];
      } else {
        // For string types, both the actual value and display value are the same
        extractionResponseTypeDisplay.value = newPrompt.response_type;
        extractionEnumOptions.value = [];
      }

      // Initialize evaluation agent fields
      evaluationPrompt.value = newPrompt.evaluation_prompt;

      // Handle response type and enum options for evaluation agent
      if (newPrompt.evaluation_response_type) {
        if (
          typeof newPrompt.evaluation_response_type === 'object' &&
          'values' in newPrompt.evaluation_response_type
        ) {
          // For enum types, set the display value
          evaluationResponseTypeDisplay.value = newPrompt.evaluation_response_type.multiple_choices
            ? 'multiple_enum'
            : 'enum';
          evaluationEnumOptions.value = [...newPrompt.evaluation_response_type.values];
        } else {
          // For string types, both the actual value and display value are the same
          evaluationResponseTypeDisplay.value = newPrompt.evaluation_response_type;
          evaluationEnumOptions.value = [];
        }
      } else {
        evaluationResponseTypeDisplay.value = null;
        evaluationEnumOptions.value = [];
      }

      // Initialize suggestion agent fields
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      suggestionPrompt.value = newPrompt.suggestion_prompt || null;
      suggestionExampleData.value = newPrompt.suggestion_example_data ?? [];
      suggestionToolSelection.value = newPrompt.suggestion_tool_selection ?? [
        SuggestionTools.READ_CLAUSE_LIBRARY,
      ];
    }
  },
  { immediate: true }
);

// Update watch for response types to initialize enum options
watch(
  () => extractionResponseTypeDisplay.value,
  newType => {
    if (
      (newType === 'enum' || newType === 'multiple_enum') &&
      extractionEnumOptions.value.length === 0
    ) {
      extractionEnumOptions.value = [{ name: '', definition: '' }];
    }
  }
);

watch(
  () => evaluationResponseTypeDisplay.value,
  newType => {
    if (
      (newType === 'enum' || newType === 'multiple_enum') &&
      evaluationEnumOptions.value.length === 0
    ) {
      evaluationEnumOptions.value = [{ name: '', definition: '', color: null }];
    }
  }
);

// Functions for managing enum options
function addExtractionEnumOption(): void {
  extractionEnumOptions.value.push({ name: '', definition: '' });
}

function removeExtractionEnumOption(index: number): void {
  extractionEnumOptions.value.splice(index, 1);
}

function addEvaluationEnumOption(): void {
  evaluationEnumOptions.value.push({ name: '', definition: '', color: null });
}

function removeEvaluationEnumOption(index: number): void {
  evaluationEnumOptions.value.splice(index, 1);
}

// Functions for managing example data
function addExampleDataItem(): void {
  extractionExampleData.value.push('');
}

function removeExampleDataItem(index: number): void {
  extractionExampleData.value.splice(index, 1);
}

// Functions for managing suggestion example data
function addSuggestionExampleDataItem(): void {
  suggestionExampleData.value.push('');
}

function removeSuggestionExampleDataItem(index: number): void {
  suggestionExampleData.value.splice(index, 1);
}

// Add an evaluation agent with default settings
const addEvaluationAgent = (): void => {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to update prompts.');
    return;
  }
  // Set evaluation prompt with default risk assessment prompt
  evaluationPrompt.value = 'Evaluate the risk posed by this result';
  // Always set response type to 'enum' for Choice type
  evaluationResponseTypeDisplay.value = 'enum';
  // Initialize with High and Low risk options with colors
  evaluationEnumOptions.value = [
    { name: 'High', definition: '', color: '#F4C7C6' }, // Red for high risk rgb(244, 199, 198)
    { name: 'Low', definition: '', color: '#CFF2CD' }, // Green for low risk
    {
      name: 'N/A',
      definition: 'Cant be assigned, because data is no match for question.',
      color: '#FFFFFF',
    }, // White for N/A
  ];
  // Switch to the evaluation tab
  activeTab.value = 'evaluation';
};

// Function to delete evaluation agent
function deleteEvaluationAgent(): void {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to update prompts.');
    return;
  }
  evaluationPrompt.value = null;
  evaluationResponseTypeDisplay.value = null;
  evaluationEnumOptions.value = [];
}

// Add a suggestion agent with default settings
const addSuggestionAgent = (): void => {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to update prompts.');
    return;
  }
  // Set suggestion prompt with default prompt
  suggestionPrompt.value = 'Suggest an amendment to the contract to enhance this clause. ';
  // Initialize with empty example data
  suggestionExampleData.value = [];
  // Switch to the suggestion tab
  activeTab.value = 'suggestion';
};

// Function to delete suggestion agent
function deleteSuggestionAgent(): void {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to update prompts.');
    return;
  }
  suggestionPrompt.value = null;
  suggestionExampleData.value = [];
}

// Function to save changes
function saveChanges(): void {
  if (!props.prompt) return;
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to update prompts.');
    return;
  }

  if (props.saveDisabled) {
    message.error('Not enough credits to save this prompt.');
    return;
  }

  try {
    // Validate extraction agent fields
    if (!extractionName.value.trim()) {
      message.error('Prompt name is required');
      return;
    }

    if (!extractionPrompt.value.trim()) {
      message.error('Extraction prompt is required');
      return;
    }

    // Prepare the final update object
    const updates: PromptUpdate = {
      name: extractionName.value,
      prompt: extractionPrompt.value,
      example_data: extractionExampleData.value,
      response_type: null, // Will be set below
      evaluation_prompt: evaluationPrompt.value,
      evaluation_response_type: null, // Will be set below
      suggestion_prompt: suggestionPrompt.value,
      suggestion_example_data: suggestionExampleData.value,
      suggestion_tool_selection: suggestionToolSelection.value,
    };

    // Handle extraction response type
    if (isExtractionEnumType.value) {
      // Validate extraction enum options
      if (extractionEnumOptions.value.length < 2) {
        message.error('At least two options are required for extraction choice types');
        return;
      }

      // Validate that all extraction enum options have names
      for (const option of extractionEnumOptions.value) {
        if (!option.name.trim()) {
          message.error('All extraction options must have a name');
          return;
        }
      }

      // Check for duplicate names in extraction options
      const extractionOptionNames = new Set<string>();
      for (const option of extractionEnumOptions.value) {
        if (extractionOptionNames.has(option.name)) {
          message.error('All extraction option names must be unique');
          return;
        }
        extractionOptionNames.add(option.name);
      }

      // Create a proper EnumResponseType object for extraction
      const enumType: EnumResponseType = {
        multiple_choices: extractionResponseTypeDisplay.value === 'multiple_enum',
        values: extractionEnumOptions.value.map(opt => ({
          name: opt.name,
          definition: opt.definition || '',
        })),
      };

      // Set the type-safe EnumResponseType
      updates.response_type = enumType;
    } else {
      // For non-enum types, assign the string type directly (which is type-safe)
      updates.response_type = extractionResponseTypeDisplay.value as NonEnumResponseType;
    }

    // Handle evaluation response type if it exists
    if (evaluationPrompt.value && evaluationResponseTypeDisplay.value) {
      if (isEvaluationEnumType.value) {
        // Validate evaluation enum options
        if (evaluationEnumOptions.value.length < 2) {
          message.error('At least two options are required for evaluation choice types');
          return;
        }

        // Validate that all evaluation enum options have names
        for (const option of evaluationEnumOptions.value) {
          if (!option.name.trim()) {
            message.error('All evaluation options must have a name');
            return;
          }
        }

        // Check for duplicate names in evaluation options
        const evaluationOptionNames = new Set<string>();
        for (const option of evaluationEnumOptions.value) {
          if (evaluationOptionNames.has(option.name)) {
            message.error('All evaluation option names must be unique');
            return;
          }
          evaluationOptionNames.add(option.name);
        }

        // Create a proper EnumResponseType object for evaluation
        const evalEnumType: EnumResponseType = {
          multiple_choices: evaluationResponseTypeDisplay.value === 'multiple_enum',
          values: evaluationEnumOptions.value.map(opt => ({
            name: opt.name,
            definition: opt.definition || '',
            color: opt.color,
          })),
        };

        // Set the type-safe EnumResponseType
        updates.evaluation_response_type = evalEnumType;
      } else {
        // For non-enum types, assign the string type directly
        updates.evaluation_response_type =
          evaluationResponseTypeDisplay.value as NonEnumEvaluationResponseType;
      }
    } else {
      updates.evaluation_response_type = null;
    }

    // Emit save event with updates
    // For new prompts, use a temporary UID or empty string
    const promptId = props.prompt.uid || 'new-prompt';
    emit('save', promptId, updates);
  } catch (error) {
    message.error('Failed to save prompt');
    logger.error('Failed to save prompt', error);
  }
}

// Function to cancel evaluation agent editing - REMOVED: No longer needed with always-editable forms
// function cancelEvaluationEditing(): void { ... }

// Add computed properties to validate option names
const hasDuplicateExtractionNames = computed<boolean>(() => {
  const names = extractionEnumOptions.value.map(opt => opt.name.trim()).filter(name => name !== '');
  return names.length !== new Set(names).size;
});

const hasDuplicateEvaluationNames = computed<boolean>(() => {
  const names = evaluationEnumOptions.value.map(opt => opt.name.trim()).filter(name => name !== '');
  return names.length !== new Set(names).size;
});

// Function to close the drawer
function closeDrawer(): void {
  // Close the drawer without saving - user must use the Save button
  showDrawer.value = false;
}

// Function to open expand modal
function openExpandModal(type: 'extraction' | 'evaluation' | 'suggestion'): void {
  expandedTextareaType.value = type;
  if (type === 'extraction') {
    expandedText.value = extractionPrompt.value;
  } else if (type === 'evaluation') {
    expandedText.value = evaluationPrompt.value ?? '';
  } else {
    expandedText.value = suggestionPrompt.value ?? '';
  }
  showExpandModal.value = true;
}

// Function to close expand modal
function closeExpandModal(): void {
  showExpandModal.value = false;
  expandedTextareaType.value = null;
  expandedText.value = '';
}

// Watch for changes in expanded text and sync back to original
watch(expandedText, newValue => {
  switch (expandedTextareaType.value) {
    case 'extraction': {
      extractionPrompt.value = newValue;

      break;
    }
    case 'evaluation': {
      evaluationPrompt.value = newValue;

      break;
    }
    case 'suggestion': {
      suggestionPrompt.value = newValue;

      break;
    }
    // No default
  }
});
</script>

<template>
  <NDrawer v-model:show="showDrawer" :width="drawerWidth" placement="right">
    <NDrawerContent :title="props.isNew ? 'Create' : 'Edit'" closable>
      <gaia-text
        size="subtitle"
        weight="semibold"
        tone="ink"
        :style="{ display: 'block', marginBottom: '16px' }"
      >
        {{ props.isNew ? 'Create Prompt' : 'Edit Prompt' }}
      </gaia-text>

      <div class="vertical-tabs-layout">
        <div class="tabs-container">
          <!-- Vertical Tabs on the Left -->
          <div class="vertical-tabs">
            <div class="tabs-scroll-area">
              <div
                class="tab-item"
                :class="{ active: activeTab === 'extraction' }"
                @click="activeTab = 'extraction'"
              >
                <div class="tab-icon">🔍</div>
                <div class="tab-content">
                  <div class="tab-title">Extraction Agent</div>
                  <div class="tab-subtitle">Extracts data from documents</div>
                </div>
              </div>

              <div
                v-if="hasEvaluationAgent"
                class="tab-item"
                :class="{ active: activeTab === 'evaluation' }"
                @click="activeTab = 'evaluation'"
              >
                <div class="tab-icon">⚖️</div>
                <div class="tab-content">
                  <div class="tab-title">Evaluation Agent</div>
                  <div class="tab-subtitle">Evaluates extracted data</div>
                </div>
              </div>

              <div
                v-if="hasSuggestionAgent"
                class="tab-item"
                :class="{ active: activeTab === 'suggestion' }"
                @click="activeTab = 'suggestion'"
              >
                <div class="tab-icon">💡</div>
                <div class="tab-content">
                  <div class="tab-title">Suggestion Agent</div>
                  <div class="tab-subtitle">Provides suggestions</div>
                </div>
              </div>

              <!-- Add Agent Buttons in Tabs -->
              <div
                v-if="!hasEvaluationAgent && authStore.userPermissions.is_org_admin"
                class="add-tab-item"
                @click="addEvaluationAgent"
              >
                <div class="tab-icon">+</div>
                <div class="tab-content">
                  <div class="tab-title">Add Evaluation Agent</div>
                  <div class="tab-subtitle">Risk assessment</div>
                </div>
              </div>

              <div
                v-if="!hasSuggestionAgent && authStore.userPermissions.is_org_admin"
                class="add-tab-item"
                @click="addSuggestionAgent"
              >
                <div class="tab-icon">+</div>
                <div class="tab-content">
                  <div class="tab-title">Add Suggestion Agent</div>
                  <div class="tab-subtitle">Improvements</div>
                </div>
              </div>
            </div>

            <AgentDrawerSidebarActions
              :save-notice="props.saveNotice"
              :can-save="canSavePrompt"
              @close="closeDrawer"
              @save="saveChanges"
            />
          </div>

          <!-- Content Area on the Right -->
          <div class="tab-content-area">
            <!-- Extraction Agent Content -->
            <div v-if="activeTab === 'extraction'" class="agent-content">
              <NCard class="agent-card extraction-agent-card">
                <template #header>
                  <div class="agent-card-header">
                    <div>Extraction Agent</div>
                    <div class="agent-card-subtitle">Extracts data from documents</div>
                  </div>
                </template>

                <div class="agent-edit">
                  <NFormItem label="Name" required>
                    <gaia-input v-model:value="extractionName" placeholder="Agent name" />
                  </NFormItem>

                  <NFormItem label="Type" required>
                    <gaia-select
                      v-model:value="extractionResponseTypeDisplay"
                      :options="typeOptions"
                      placeholder="Select response type"
                    />
                  </NFormItem>

                  <NFormItem label="Prompt" required>
                    <gaia-textarea
                      v-model:value="extractionPrompt"
                      :rows="4"
                      placeholder="Enter extraction prompt"
                      :autosize="false"
                    />
                  </NFormItem>
                  <div style="text-align: right">
                    <NButton size="small" secondary @click="openExpandModal('extraction')">
                      Expand
                    </NButton>
                  </div>

                  <!-- Example Data section -->
                  <NFormItem label="Example Data (Optional)">
                    <NSpace vertical>
                      <NText type="info" style="margin-bottom: 8px">
                        Add example data that the AI can reference. You can refer to this in your
                        prompt using phrases like "check the example data for...".
                      </NText>
                      <div
                        v-for="(_, index) in extractionExampleData"
                        :key="index"
                        class="example-data-item"
                      >
                        <div class="example-data-row">
                          <gaia-textarea
                            v-model:value="extractionExampleData[index]"
                            :rows="2"
                            placeholder="Enter example data item"
                            class="example-data-input"
                            :autosize="false"
                          />
                          <NButton
                            circle
                            type="error"
                            class="example-data-remove-btn"
                            @click="removeExampleDataItem(index)"
                          >
                            <template #icon>
                              <n-icon :component="IconClose" :size="16" />
                            </template>
                          </NButton>
                        </div>
                      </div>

                      <NButton @click="addExampleDataItem">Add Example Data</NButton>
                    </NSpace>
                  </NFormItem>

                  <!-- Enum options section for extraction -->
                  <NFormItem v-if="isExtractionEnumType" label="Options" required>
                    <NSpace vertical>
                      <NText type="info" style="margin-bottom: 8px"
                        >At least 2 options are required for choice types.</NText
                      >
                      <NText
                        v-if="hasDuplicateExtractionNames"
                        type="error"
                        style="margin-bottom: 8px"
                      >
                        Error: Option names must be unique.
                      </NText>
                      <div
                        v-for="(option, index) in extractionEnumOptions"
                        :key="index"
                        class="enum-option"
                      >
                        <NSpace align="center">
                          <gaia-input
                            v-model:value="option.name"
                            placeholder="Option name"
                            :status="
                              hasDuplicateExtractionNames &&
                              extractionEnumOptions.filter(
                                o => o.name.trim() === option.name.trim()
                              ).length > 1
                                ? 'error'
                                : undefined
                            "
                          />
                          <gaia-input
                            v-model:value="option.definition"
                            placeholder="Option description"
                          />
                          <NButton
                            circle
                            type="error"
                            :disabled="extractionEnumOptions.length <= 1"
                            @click="removeExtractionEnumOption(index)"
                          >
                            <template #icon>
                              <n-icon :component="IconClose" :size="16" />
                            </template>
                          </NButton>
                        </NSpace>
                      </div>

                      <NButton @click="addExtractionEnumOption">Add Option</NButton>
                    </NSpace>
                  </NFormItem>
                </div>
              </NCard>
            </div>

            <!-- Evaluation Agent Content -->
            <div v-if="activeTab === 'evaluation' && hasEvaluationAgent" class="agent-content">
              <NCard class="agent-card evaluation-agent-card">
                <template #header>
                  <div class="agent-card-header">
                    <span>Evaluation Agent</span>
                    <div class="agent-card-subtitle">
                      Evaluates extracted data for risk assessment
                    </div>
                  </div>
                </template>

                <div class="agent-edit">
                  <NFormItem label="Prompt" required>
                    <gaia-textarea
                      v-model:value="evaluationPrompt"
                      :rows="4"
                      placeholder="Enter evaluation prompt"
                      :autosize="false"
                    />
                  </NFormItem>
                  <div style="text-align: right">
                    <NButton size="small" secondary @click="openExpandModal('evaluation')">
                      Expand
                    </NButton>
                  </div>

                  <!-- Enum options section for evaluation -->
                  <NFormItem label="Risk Levels" required style="width: 100%">
                    <NSpace vertical style="width: 100%">
                      <NText style="margin-bottom: 8px">
                        Define the risk levels that the AI can assign. Names should be concise
                        labels. Colors help visually distinguish risk levels.
                      </NText>
                      <NText
                        v-if="hasDuplicateEvaluationNames"
                        type="error"
                        style="margin-bottom: 8px"
                      >
                        Error: Option names must be unique.
                      </NText>
                      <div
                        v-for="(option, index) in evaluationEnumOptions"
                        :key="index"
                        class="enum-option"
                      >
                        <NGrid :cols="24" :x-gap="16" :y-gap="16" responsive="screen">
                          <NGridItem :span="7">
                            <gaia-input
                              v-model:value="option.name"
                              placeholder="Risk level name"
                              :status="
                                hasDuplicateEvaluationNames &&
                                evaluationEnumOptions.filter(
                                  o => o.name.trim() === option.name.trim()
                                ).length > 1
                                  ? 'error'
                                  : undefined
                              "
                            />
                          </NGridItem>
                          <NGridItem :span="12">
                            <gaia-input
                              v-model:value="option.definition"
                              placeholder="Description (optional)"
                            />
                          </NGridItem>
                          <NGridItem :span="3">
                            <NColorPicker v-model:value="option.color" :show-alpha="false" />
                          </NGridItem>
                          <NGridItem :span="2">
                            <NButton
                              circle
                              type="error"
                              :disabled="evaluationEnumOptions.length <= 1"
                              @click="removeEvaluationEnumOption(index)"
                            >
                              <template #icon>
                                <n-icon :component="IconClose" :size="16" />
                              </template>
                            </NButton>
                          </NGridItem>
                        </NGrid>
                      </div>

                      <NButton @click="addEvaluationEnumOption">Add Risk Level</NButton>
                    </NSpace>
                  </NFormItem>

                  <div class="agent-actions">
                    <NPopconfirm @positive-click="deleteEvaluationAgent">
                      <template #trigger>
                        <NButton
                          type="error"
                          secondary
                          size="medium"
                          :disabled="!authStore.userPermissions.is_org_admin"
                        >
                          Delete Evaluation Agent
                        </NButton>
                      </template>
                      <span>Are you sure you want to remove the evaluation agent?</span>
                    </NPopconfirm>
                  </div>
                </div>
              </NCard>
            </div>

            <!-- Suggestion Agent Content -->
            <div v-if="activeTab === 'suggestion' && hasSuggestionAgent" class="agent-content">
              <NCard class="agent-card suggestion-agent-card">
                <template #header>
                  <div class="agent-card-header">
                    <span>Suggestion Agent</span>
                    <div class="agent-card-subtitle">
                      Prompt this agent to provide suggestions to redline the contract. Agent has
                      access to other agents outputs and the contract.
                    </div>
                  </div>
                </template>

                <div class="agent-edit">
                  <NFormItem label="Prompt" required>
                    <gaia-textarea
                      v-model:value="suggestionPrompt"
                      :rows="4"
                      placeholder="Enter suggestion prompt"
                      :autosize="false"
                    />
                  </NFormItem>
                  <div style="text-align: right">
                    <NButton size="small" secondary @click="openExpandModal('suggestion')">
                      Expand
                    </NButton>
                  </div>

                  <!-- Example Data section for suggestions -->
                  <NFormItem label="Example Data (Optional)">
                    <NSpace vertical>
                      <NText style="margin-bottom: 8px">
                        Add example data for the AI, e.g. a clause or a section of the contract.
                      </NText>
                      <div
                        v-for="(_, index) in suggestionExampleData"
                        :key="index"
                        class="example-data-item"
                      >
                        <div class="example-data-row">
                          <gaia-textarea
                            v-model:value="suggestionExampleData[index]"
                            :rows="2"
                            placeholder="Enter example suggestion"
                            class="example-data-input"
                            :autosize="false"
                          />
                          <NButton
                            circle
                            type="error"
                            class="example-data-remove-btn"
                            @click="removeSuggestionExampleDataItem(index)"
                          >
                            <template #icon>
                              <n-icon :component="IconClose" :size="16" />
                            </template>
                          </NButton>
                        </div>
                      </div>

                      <NButton @click="addSuggestionExampleDataItem"
                        >Add Example Suggestion</NButton
                      >
                    </NSpace>
                  </NFormItem>

                  <!-- Tool Selection -->
                  <NFormItem label="Available Tools">
                    <NSpace vertical>
                      <NText style="margin-bottom: 8px; color: #666; font-size: 13px">
                        Select which tools the suggestion agent can use to enhance its suggestions.
                      </NText>
                      <gaia-checkbox-group v-model:value="suggestionToolSelection">
                        <NSpace vertical>
                          <gaia-checkbox :value="'read_clause_library'" label="Clause Library">
                            <div style="display: flex; flex-direction: column">
                              <span style="font-weight: 500">Clause Library</span>
                              <span style="font-size: 12px; color: #999">
                                Access pre-approved clauses from your organization's library
                              </span>
                            </div>
                          </gaia-checkbox>
                          <gaia-checkbox :value="'web_search'" label="Web Search">
                            <div style="display: flex; flex-direction: column">
                              <span style="font-weight: 500">Web Search</span>
                              <span style="font-size: 12px; color: #999">
                                Search the web for relevant information and best practices
                              </span>
                            </div>
                          </gaia-checkbox>
                        </NSpace>
                      </gaia-checkbox-group>
                    </NSpace>
                  </NFormItem>

                  <div class="agent-actions">
                    <NPopconfirm @positive-click="deleteSuggestionAgent">
                      <template #trigger>
                        <NButton
                          type="error"
                          secondary
                          size="medium"
                          :disabled="!authStore.userPermissions.is_org_admin"
                        >
                          Delete Suggestion Agent
                        </NButton>
                      </template>
                      <span>Are you sure you want to remove the suggestion agent?</span>
                    </NPopconfirm>
                  </div>
                </div>
              </NCard>
            </div>
          </div>
        </div>
      </div>
    </NDrawerContent>
  </NDrawer>

  <!-- Expand Modal -->
  <NModal
    v-model:show="showExpandModal"
    preset="card"
    :title="`${expandedTextareaType === 'extraction' ? 'Extraction' : 'Evaluation'} Prompt Editor`"
    style="width: 95%; max-width: 1400px; height: 90vh"
    :mask-closable="false"
  >
    <div
      v-if="expandedTextareaType"
      style="height: 100%; display: flex; flex-direction: column; overflow: hidden"
    >
      <div style="flex: 1; display: flex; flex-direction: column; min-height: 0; overflow: hidden">
        <NText depth="3" style="margin-bottom: 16px; flex-shrink: 0">
          Edit your {{ expandedTextareaType === 'extraction' ? 'extraction' : 'evaluation' }} prompt
          in the large text area below. Changes are automatically synced with the main form.
        </NText>

        <div
          style="flex: 1; display: flex; flex-direction: column; min-height: 0; overflow: hidden"
        >
          <textarea
            v-model="expandedText"
            aria-label="Prompt"
            placeholder="Enter your prompt here..."
            style="
              flex: 1;
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
              font-size: 14px;
              line-height: 1.5;
              padding: 12px;
              border: 1px solid #d9d9d9;
              border-radius: 6px;
              resize: vertical;
              min-height: 400px;
              overflow-y: auto;
              background: white;
            "
          />
        </div>
      </div>

      <div style="margin-top: 16px; text-align: right; flex-shrink: 0">
        <NButton @click="closeExpandModal">Close</NButton>
      </div>
    </div>
  </NModal>
</template>

<style scoped>
.agent-card {
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  box-shadow: none;
  position: relative;
}

.extraction-agent-card {
  border-color: #2080f0;
}

.evaluation-agent-card {
  border-color: #f0a020;
}

.agent-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.prompt-content {
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.agent-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.enum-option {
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background-color: rgba(0, 0, 0, 0.01);
  transition: all 0.2s ease;
  width: 100%;
}

.enum-option :deep(.n-space) {
  width: 100%;
}

.enum-option :deep(.n-input) {
  flex: 1;
}

.enum-option :deep(.n-button) {
  flex-shrink: 0;
}

.example-data-item {
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background-color: rgba(0, 0, 0, 0.01);
  transition: all 0.2s ease;
  width: 100%;
}

.example-data-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.example-data-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.example-data-input {
  flex: 1;
  min-width: 0;
}

.example-data-remove-btn {
  flex-shrink: 0;
  margin-top: 4px;
}

.example-data-item :deep(.n-button) {
  flex-shrink: 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.option-item {
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.drawer-actions {
  flex-shrink: 0;
  margin-top: 16px;
  padding: 16px 0 0 0;
  border-top: 1px solid var(--n-border-color);
  background: var(--n-color);
}

.drawer-actions .n-button {
  min-width: 120px;
}

.add-evaluation-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px 0;
  position: relative;
}

.add-evaluation-button {
  font-weight: 500;
  color: #f0a020;
  border-color: #f0a020;
  background-color: rgba(240, 160, 32, 0.1);
  position: relative;
  z-index: 2;
}

.add-evaluation-button:hover {
  background-color: rgba(240, 160, 32, 0.2);
}

/* Add focus indicator for input fields */
:deep(.n-input:focus-within) {
  box-shadow: 0 0 0 2px rgba(32, 128, 240, 0.2);
}

.agent-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 4px;
}

.agent-edit {
  padding: 8px 4px;
}

.prompt-content {
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* Improve option styling */
.enum-option {
  border: 1px solid var(--n-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background-color: rgba(0, 0, 0, 0.01);
  transition: all 0.2s ease;
}

.enum-option:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.agent-card-header {
  display: flex;
  flex-direction: column;
}

.agent-card-subtitle {
  font-size: 0.85em;
  color: var(--n-text-color-3);
  font-weight: normal;
  margin-top: 2px;
}

.agent-connection {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 16px 0;
  padding: 0 0 0 20px; /* Add left padding for timeline alignment */
}

.agent-timeline {
  width: 100%;
}

/* Override timeline styling to position it correctly */
:deep(.n-timeline) {
  padding-left: 0;
}

:deep(.n-timeline-item) {
  position: relative;
}

:deep(.n-timeline-item-content) {
  margin-top: 0;
  min-height: 20px;
}

:deep(.n-timeline-item-content__content) {
  color: var(--n-text-color-2);
}

:deep(.n-timeline-item-content__title) {
  font-weight: bold;
}

:deep(.n-timeline-item__line--dashed) {
  border-left-style: dashed;
  border-left-width: 2px;
}

/* Hide the old connection visualization styles that are no longer needed */
.connection-line,
.result-object,
.result-label {
  display: none;
}

.evaluation-type-info {
  padding: 12px;
  border-radius: 8px;
  background-color: rgba(240, 160, 32, 0.08);
  margin-bottom: 16px;
}

.option-with-color {
  display: flex;
  align-items: center;
}

.color-square {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.pdf-viewer {
  width: 100%;
  height: auto;
}

.docx-viewer {
  width: 100%;
  height: 100%;
  border: 1px solid var(--n-border-color);
  border-radius: 4px;
}

.unsupported-file {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--n-text-color-3);
}

.docx-container {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.word-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

:deep(.vue-pdf-embed) {
  max-width: 100%;
  height: auto;
}

:deep(.vue-office-docx) {
  height: 100%;
  width: 100%;
  overflow: auto !important;
  border: 1px solid #eee;
  border-radius: 4px;
  max-height: 100%;
  display: block;
}

.non-pdf-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: #666;
}

.unsupported-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
  border: 1px dashed #ccc;
  height: 300px;
}

.unsupported-file h3 {
  margin: 0 0 12px 0;
  color: #333;
}

.unsupported-file p {
  margin: 4px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

/* Modal textarea styling */
.expanded-textarea {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
}

/* Vertical Tabs Layout */
.vertical-tabs-layout {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  overflow: hidden;
}

.tabs-container {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.vertical-tabs {
  width: 200px;
  flex-shrink: 0;
  background-color: var(--n-color);
  border-right: 1px solid var(--n-border-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tabs-scroll-area {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
  min-height: 0;
}

.tab-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  margin: 2px 6px;
  border-radius: 4px;
}

.tab-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.tab-item.active {
  background-color: rgba(32, 128, 240, 0.1);
  border-left-color: #2080f0;
  color: #2080f0;
}

.tab-icon {
  font-size: 16px;
  margin-right: 8px;
  flex-shrink: 0;
}

.tab-content {
  flex: 1;
  min-width: 0;
}

.tab-title {
  font-weight: 500;
  font-size: 13px;
  line-height: 1.2;
  margin-bottom: 1px;
}

.tab-subtitle {
  font-size: 11px;
  color: var(--n-text-color-3);
  line-height: 1.1;
}

.add-tab-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  margin: 8px 6px 2px 6px;
  border-radius: 4px;
  border: 1px dashed var(--n-border-color);
  background-color: transparent;
  opacity: 0.6;
}

.add-tab-item:hover {
  background-color: rgba(32, 128, 240, 0.05);
  border-color: #2080f0;
  border-style: solid;
  color: #2080f0;
  opacity: 1;
}

.add-tab-item .tab-icon {
  font-size: 14px;
  font-weight: bold;
}

.add-tab-item .tab-title {
  font-weight: 400;
  font-style: italic;
}

.tab-content-area {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: var(--n-color);
  min-height: 0;
}

.agent-content {
  max-width: 100%;
  padding-bottom: 20px;
}

.suggestion-agent-card {
  border-color: #18a058;
}

.add-suggestion-button {
  font-weight: 500;
  color: #18a058;
  border-color: #18a058;
  background-color: rgba(24, 160, 88, 0.1);
}

.add-suggestion-button:hover {
  background-color: rgba(24, 160, 88, 0.2);
}
</style>
